import { useState } from 'react';
import { Switch } from '@headlessui/react';
import { 
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  BellIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

function SalesAutomation() {
  const [automations, setAutomations] = useState({
    emailFollowup: true,
    chatbot: false,
    notifications: true,
    callCenter: false
  });

  const [selectedTrigger, setSelectedTrigger] = useState('abandoned_cart');

  const triggers = [
    { id: 'abandoned_cart', name: 'Abandoned Cart' },
    { id: 'product_view', name: 'Product View' },
    { id: 'signup', name: 'New Signup' },
    { id: 'first_purchase', name: 'First Purchase' }
  ];

  const automationTypes = [
    {
      id: 'emailFollowup',
      name: 'Email Follow-up',
      description: 'Automated email sequences based on user behavior',
      icon: EnvelopeIcon
    },
    {
      id: 'chatbot',
      name: 'AI Chatbot',
      description: 'Intelligent chat responses to common questions',
      icon: ChatBubbleLeftIcon
    },
    {
      id: 'notifications',
      name: 'Push Notifications',
      description: 'Targeted browser and mobile notifications',
      icon: BellIcon
    },
    {
      id: 'callCenter',
      name: 'Call Center Integration',
      description: 'Automated call scheduling and follow-up',
      icon: PhoneIcon
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sales Automation</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure automated responses to customer actions
        </p>
      </div>

      {/* Trigger Selection */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Select Trigger</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {triggers.map((trigger) => (
            <button
              key={trigger.id}
              onClick={() => setSelectedTrigger(trigger.id)}
              className={`p-4 rounded-lg border-2 text-left ${
                selectedTrigger === trigger.id
                  ? 'border-[#0049ac] bg-[#0049ac]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {trigger.name}
            </button>
          ))}
        </div>
      </div>

      {/* Automation Types */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Configure Automations</h2>
        <div className="space-y-4">
          {automationTypes.map((type) => (
            <div
              key={type.id}
              className="flex items-center justify-between p-4 rounded-lg border border-gray-200"
            >
              <div className="flex items-center space-x-4">
                <div className="p-2 bg-[#0049ac]/10 rounded-lg">
                  <type.icon className="h-6 w-6 text-[#0049ac]" />
                </div>
                <div>
                  <h3 className="font-medium">{type.name}</h3>
                  <p className="text-sm text-gray-500">{type.description}</p>
                </div>
              </div>
              <Switch
                checked={automations[type.id]}
                onChange={(checked) => 
                  setAutomations(prev => ({ ...prev, [type.id]: checked }))
                }
                className={`${
                  automations[type.id] ? 'bg-[#0049ac]' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 items-center rounded-full`}
              >
                <span className="sr-only">Enable {type.name}</span>
                <span
                  className={`${
                    automations[type.id] ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                />
              </Switch>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          className="px-4 py-2 bg-[#0049ac] text-white rounded-lg hover:bg-[#0049ac]/90"
        >
          Save Automation Rules
        </button>
      </div>
    </div>
  );
}

export default SalesAutomation;